import { ArrowRight } from 'lucide-react';

import { PersonaAvatar } from '../persona/persona-avatar';
import type { Spotlight } from '../store/research-room-store';

/** How far in from the board's edge the hint sits, in screen pixels. */
const INSET_PX = 28;

/**
 * Where a lit place is, when it is off the part of the board in view: a pill
 * at the edge, its arrow pointing the way, saying who is talking about what.
 * Clicking it goes there — the only way a place the room raised moves the
 * board.
 */
export function SpotEdgeHint({
  frame,
  onShow,
  soft = false,
  spot,
  target,
}: {
  /** For a hovered line: light, so it does not flash dark at the edge. */
  soft?: boolean;
  /** The part of the board in view, in screen pixels. */
  frame: { height: number; width: number };
  onShow: () => void;
  spot: Spotlight;
  /** The lit place's centre, in screen pixels relative to the board. */
  target: { x: number; y: number };
}) {
  const centre = { x: frame.width / 2, y: frame.height / 2 };
  const angle = Math.atan2(target.y - centre.y, target.x - centre.x);
  // Along the line from the middle of the view towards the place, stopped
  // at the edge.
  const reach = Math.min(
    (frame.width / 2 - INSET_PX) / Math.max(Math.abs(Math.cos(angle)), 1e-6),
    (frame.height / 2 - INSET_PX) / Math.max(Math.abs(Math.sin(angle)), 1e-6),
  );
  const at = {
    x: centre.x + Math.cos(angle) * reach,
    y: centre.y + Math.sin(angle) * reach,
  };
  const { focus, speaker } = spot;

  return (
    <button
      aria-label={`Show ${focus.label} on the board`}
      className={[
        'absolute z-10 flex -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center gap-1.5 rounded-pill py-1 pr-2.5 pl-1 text-tag whitespace-nowrap shadow-popover transition duration-300 animate-in fade-in motion-reduce:animate-none',
        soft
          ? 'bg-surface text-text-primary ring-1 ring-hairline hover:bg-surface-hover'
          : 'bg-ink text-white hover:bg-ink/90',
      ].join(' ')}
      onClick={onShow}
      onPointerDown={(event) => {
        event.stopPropagation();
      }}
      style={{ left: at.x, top: at.y }}
      type="button"
    >
      {speaker ? <PersonaAvatar persona={speaker} size="sm" /> : null}
      <span className="max-w-40 truncate">
        {speaker ? `${speaker.name.split(' ')[0]} · ` : ''}
        {focus.label}
      </span>
      <ArrowRight
        aria-hidden="true"
        className="size-3.5 shrink-0"
        style={{ transform: `rotate(${String(angle)}rad)` }}
      />
    </button>
  );
}
